class BrSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.index = 0;
    if (!this.track || this.slides.length < 2) return;

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

    let ticking = false;
    this.track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        this.sync();
        ticking = false;
      });
    });

    ['mouseenter', 'focusin', 'touchstart'].forEach((type) =>
      this.addEventListener(type, () => this.stop(), { passive: true })
    );
    ['mouseleave', 'focusout'].forEach((type) => this.addEventListener(type, () => this.play()));

    // Banners have different shapes, so match the track height to the active slide.
    this.querySelectorAll('img').forEach((img) => img.addEventListener('load', () => this.fitHeight()));
    window.addEventListener('resize', () => this.fitHeight());

    this.sync();
    this.classList.add('is-ready');
    this.play();
  }

  fitHeight() {
    const slide = this.slides[this.index];
    if (slide) this.track.style.height = `${slide.offsetHeight}px`;
  }

  disconnectedCallback() {
    this.stop();
  }

  go(index) {
    const count = this.slides.length;
    this.index = (index + count) % count;
    const slide = this.slides[this.index];
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    this.setActive();
  }

  sync() {
    const left = this.track.scrollLeft;
    let closest = 0;
    let distance = Infinity;
    this.slides.forEach((slide, i) => {
      const d = Math.abs(slide.offsetLeft - this.track.offsetLeft - left);
      if (d < distance) {
        distance = d;
        closest = i;
      }
    });
    this.index = closest;
    this.setActive();
  }

  setActive() {
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
    this.fitHeight();
  }

  play() {
    if (this.dataset.autoplay !== 'true' || Shopify.designMode) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stop();
    this.timer = setInterval(() => this.go(this.index + 1), (parseInt(this.dataset.speed) || 5) * 1000);
  }

  stop() {
    clearInterval(this.timer);
  }
}

if (!customElements.get('br-slider')) customElements.define('br-slider', BrSlider);

class BrPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('.br-popup');
    if (!this.dialog) return;
    this.key = this.dataset.key;

    this.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.querySelector('[data-accept]')?.addEventListener('click', () => this.remember());
    this.onKeydown = (event) => {
      if (event.key === 'Escape') this.close();
    };

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        if (this.closest('.shopify-section')?.contains(event.target)) this.open();
      });
      document.addEventListener('shopify:section:deselect', () => this.close(false));
      return;
    }

    if (this.seenRecently()) return;
    this.timer = setTimeout(() => this.showWhenClear(), (parseInt(this.dataset.delay) || 0) * 1000);
  }

  // The corner pop-up waits for a centered pop-up to be closed, so they never overlap.
  showWhenClear() {
    const blocked = 'corner' in this.dataset && document.querySelector('.br-popup:not([hidden]):not(.br-popup--corner)');
    if (!blocked) return this.open();
    document.addEventListener('br-popup:closed', () => this.showWhenClear(), { once: true });
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('keydown', this.onKeydown);
  }

  // With 0 days the pop-up shows on every site open or refresh, but not on clicks between pages.
  seenRecently() {
    const days = parseInt(this.dataset.days);
    if (days === 0) {
      if (performance.getEntriesByType('navigation')[0]?.type === 'reload') return false;
      return document.referrer.startsWith(location.origin);
    }
    try {
      const seen = parseInt(localStorage.getItem(this.key));
      return seen && Date.now() - seen < days * 86400000;
    } catch (e) {
      return false;
    }
  }

  remember() {
    if (parseInt(this.dataset.days) === 0) return;
    try {
      localStorage.setItem(this.key, String(Date.now()));
    } catch (e) {}
  }

  open() {
    this.lastFocus = document.activeElement;
    this.dialog.hidden = false;
    requestAnimationFrame(() => this.dialog.classList.add('is-open'));
    document.addEventListener('keydown', this.onKeydown);
    if (!('corner' in this.dataset)) this.querySelector('.br-popup__close')?.focus();
  }

  close(save = true) {
    if (this.dialog.hidden) return;
    if (save) this.remember();
    this.dialog.classList.remove('is-open');
    this.dialog.hidden = true;
    document.removeEventListener('keydown', this.onKeydown);
    if (!('corner' in this.dataset)) this.lastFocus?.focus?.();
    document.dispatchEvent(new CustomEvent('br-popup:closed'));
  }
}

if (!customElements.get('br-popup')) customElements.define('br-popup', BrPopup);

/* Shop page: grid columns switch */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-br-cols]');
  if (!button) return;
  const grid = document.querySelector('[data-br-grid]');
  if (!grid) return;
  grid.style.setProperty('--br-cols', button.dataset.brCols);
  document.querySelectorAll('[data-br-cols]').forEach((el) => el.classList.toggle('is-active', el === button));
  try {
    localStorage.setItem('br-shop-cols', button.dataset.brCols);
  } catch (e) {}
});

document.addEventListener('DOMContentLoaded', () => {
  let saved = null;
  try {
    saved = localStorage.getItem('br-shop-cols');
  } catch (e) {}
  if (saved) document.querySelector(`[data-br-cols="${saved}"]`)?.click();
});

/* Shop page: load more products */
document.addEventListener('click', async (event) => {
  const link = event.target.closest('[data-br-load-more]');
  if (!link) return;
  event.preventDefault();
  if (link.classList.contains('loading')) return;
  link.classList.add('loading');
  const label = link.textContent;
  link.textContent = 'Loading...';
  try {
    const response = await fetch(link.href);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const grid = document.querySelector('[data-br-grid]');
    html.querySelectorAll('[data-br-grid] > *').forEach((item) => grid.appendChild(item));
    const next = html.querySelector('[data-br-load-more]');
    if (next) {
      link.href = next.href;
      link.textContent = label;
      link.classList.remove('loading');
    } else {
      link.closest('.br-load-more').remove();
    }
  } catch (e) {
    window.location = link.href;
  }
});

/* Keep the mobile toolbar cart count in sync with the cart */
if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    fetch(`${window.Shopify.routes.root}cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        document.querySelectorAll('[data-br-cart-count]').forEach((el) => (el.textContent = cart.item_count));
      })
      .catch(() => {});
  });
}

/* Shop page: dual handle price range */
class BrPriceRange extends HTMLElement {
  connectedCallback() {
    this.min = this.querySelector('[data-min]');
    this.max = this.querySelector('[data-max]');
    const form = this.closest('form');
    this.minLabel = form?.querySelector('[data-min-label]');
    this.maxLabel = form?.querySelector('[data-max-label]');
    if (!this.min || !this.max) return;
    this.min.addEventListener('input', () => this.update(this.min));
    this.max.addEventListener('input', () => this.update(this.max));
    this.update();
  }

  update(source) {
    let low = Number(this.min.value);
    let high = Number(this.max.value);
    if (low > high) {
      if (source === this.min) this.max.value = high = low;
      else this.min.value = low = high;
    }
    const total = Number(this.max.max) || 1;
    this.style.setProperty('--br-min', `${(low / total) * 100}%`);
    this.style.setProperty('--br-max', `${(high / total) * 100}%`);
    if (this.minLabel) this.minLabel.textContent = low.toLocaleString('en-IN');
    if (this.maxLabel) this.maxLabel.textContent = high.toLocaleString('en-IN');
  }
}

if (!customElements.get('br-price-range')) customElements.define('br-price-range', BrPriceRange);

/* Wishlist: saved in the browser, shared by every heart button and the wishlist page */
const BrWishlist = {
  key: 'br-wishlist',
  get() {
    try {
      return JSON.parse(localStorage.getItem(this.key)) || [];
    } catch (e) {
      return [];
    }
  },
  set(handles) {
    try {
      localStorage.setItem(this.key, JSON.stringify(handles));
    } catch (e) {}
    this.render();
    document.dispatchEvent(new CustomEvent('br:wishlist', { detail: { handles } }));
  },
  toggle(handle) {
    const handles = this.get();
    const index = handles.indexOf(handle);
    if (index > -1) handles.splice(index, 1);
    else handles.push(handle);
    this.set(handles);
  },
  render() {
    const handles = this.get();
    document.querySelectorAll('[data-br-wishlist]').forEach((button) => {
      const active = handles.includes(button.dataset.brWishlist);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active);
      const label = button.querySelector('[data-br-wishlist-label]');
      if (label) label.textContent = active ? 'Remove from wishlist' : 'Add to wishlist';
    });
    document.querySelectorAll('[data-br-wishlist-count]').forEach((count) => {
      count.textContent = handles.length;
      count.hidden = handles.length === 0;
    });
  },
};

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-br-wishlist]');
  if (!button) return;
  event.preventDefault();
  BrWishlist.toggle(button.dataset.brWishlist);
});

document.addEventListener('DOMContentLoaded', () => BrWishlist.render());

class BrWishlistPage extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-list]');
    this.empty = this.querySelector('[data-empty]');
    this.load();
    document.addEventListener('br:wishlist', (event) => {
      this.list.querySelectorAll('[data-handle]').forEach((item) => {
        if (!event.detail.handles.includes(item.dataset.handle)) item.remove();
      });
      this.empty.hidden = event.detail.handles.length > 0;
    });
  }

  async load() {
    const handles = BrWishlist.get();
    this.empty.hidden = handles.length > 0;
    const cards = await Promise.all(
      handles.map((handle) =>
        fetch(`${this.dataset.root}products/${handle}?sections=br-card-ajax`)
          .then((response) => (response.ok ? response.json() : null))
          .then((data) => ({ handle, html: data && data['br-card-ajax'] }))
          .catch(() => ({ handle, html: null }))
      )
    );
    const missing = [];
    cards.forEach(({ handle, html }) => {
      const card = html && new DOMParser().parseFromString(html, 'text/html').querySelector('.br-card');
      if (!card) return missing.push(handle);
      card.dataset.handle = handle;
      this.list.appendChild(card);
    });
    if (missing.length) BrWishlist.set(handles.filter((handle) => !missing.includes(handle)));
    else BrWishlist.render();
  }
}

if (!customElements.get('br-wishlist-page')) customElements.define('br-wishlist-page', BrWishlistPage);

/* My account: sidebar tabs */
class BrAccount extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const link = event.target.closest('[data-br-tab]');
      if (!link) return;
      event.preventDefault();
      this.show(link.dataset.brTab, true);
    });
    window.addEventListener('hashchange', () => this.show(location.hash.slice(1)));
    this.show(location.hash.slice(1));
  }

  show(name, push) {
    const panel = this.querySelector(`[data-br-panel="${name}"]`) ? name : 'dashboard';
    this.querySelectorAll('[data-br-panel]').forEach((el) => (el.hidden = el.dataset.brPanel !== panel));
    this.querySelectorAll('.br-account__nav [data-br-tab]').forEach((el) =>
      el.classList.toggle('is-active', el.dataset.brTab === panel)
    );
    if (push) history.replaceState(null, '', `#${panel}`);
  }
}

if (!customElements.get('br-account')) customElements.define('br-account', BrAccount);
